import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIdempotencyKey } from '@/api/idempotency'
import { useModels } from '@/api/models'
import { usePools } from '@/api/pools'
import { useProduct } from '@/api/products'
import { useArchiveVariant, useUnarchiveVariant, useVariant } from '@/api/variants'
import type { Variant } from '@/api/variants'
import { useWorkspace } from '@/api/workspace'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { variantErrorMessage } from '@/components/variants/error-message'
import { VariantForm } from '@/components/variants/variant-form'

// Variant edit (UI-SCREENS P-09, #102): the form, plus Archive / Un-archive (G1, #98 Q5). The
// History section moved to the Variant detail page (P-10) when it shipped with 05b (#98 Q16,
// #105). Owner only; the API refuses Staff too.
export const Route = createFileRoute('/w/$slug/variants/$variantId/edit')({
  component: EditVariant,
  staticData: { breadcrumb: 'variants.form.editTitle' },
})

function EditVariant() {
  const { t } = useTranslation()
  const { slug, variantId } = Route.useParams()
  const workspace = useWorkspace(slug)
  const variant = useVariant(slug, Number(variantId))
  // The Product and its Models load once the Variant says which Product it belongs to.
  const productId = variant.data?.product_id ?? 0
  const product = useProduct(slug, productId, { enabled: Boolean(variant.data) })
  const models = useModels(slug, productId, { enabled: Boolean(variant.data) })
  const pools = usePools(slug, { archived: true })

  if (workspace.data && workspace.data.role !== 'owner') {
    return <Navigate to="/w/$slug/products" params={{ slug }} replace />
  }

  const ready = variant.data && product.data && models.data && pools.data
  const failed = variant.isError || (variant.data && (product.isError || models.isError)) || pools.isError

  return (
    <section className="flex flex-col gap-4">
      <h1 className="font-heading text-xl font-semibold">{t('variants.form.editTitle')}</h1>
      {failed ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !ready ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : (
        <>
          <Link
            to="/w/$slug/products/$productId"
            params={{ slug, productId: String(product.data!.id) }}
            search={{ tab: 'variants' }}
            className="text-sm underline-offset-4 hover:underline"
          >
            {t('variants.back')}
          </Link>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span>
              {product.data!.code} · {product.data!.name}
            </span>
            {variant.data!.archived_at ? <Badge variant="outline">{t('variants.archived')}</Badge> : null}
          </div>
          <VariantForm
            slug={slug}
            product={product.data!}
            models={models.data!}
            pools={pools.data!}
            variant={variant.data!}
          />
          <ArchiveActions slug={slug} variant={variant.data!} />
        </>
      )}
    </section>
  )
}

function ArchiveActions({ slug, variant }: { slug: string; variant: Variant }) {
  const { t } = useTranslation()
  const archive = useArchiveVariant()
  const unarchive = useUnarchiveVariant()
  const archiveKey = useIdempotencyKey()
  const unarchiveKey = useIdempotencyKey()
  const [confirming, setConfirming] = useState<'archive' | 'unarchive' | null>(null)
  const [notice, setNotice] = useState('')
  const name = variant.name
  const busy = archive.isPending || unarchive.isPending
  const error = archive.isError ? archive.error : unarchive.isError ? unarchive.error : null

  function run(action: 'archive' | 'unarchive') {
    setConfirming(null)
    const key = action === 'archive' ? archiveKey : unarchiveKey
    const mutation = action === 'archive' ? archive : unarchive
    mutation.mutate(
      { params: { path: { slug, variant_id: variant.id }, header: key.headerFor({ id: variant.id }) } },
      {
        onSuccess: () => setNotice(t(`variants.archive.${action}d`, { name })),
        onSettled: (_data, mutationError) => key.settle(mutationError),
      },
    )
  }

  if (!variant.can.archive && !variant.can.unarchive) return null

  return (
    <div className="flex flex-col gap-3 border-t pt-4">
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {notice}
      </p>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {error ? variantErrorMessage(t, error) : ''}
      </p>
      <div className="flex flex-wrap gap-2">
        {variant.can.archive ? (
          <Button variant="ghost" className="h-11 text-destructive" disabled={busy} onClick={() => setConfirming('archive')}>
            {t('variants.archive.archive')}
          </Button>
        ) : null}
        {variant.can.unarchive ? (
          <Button variant="outline" className="h-11" disabled={busy} onClick={() => setConfirming('unarchive')}>
            {t('variants.archive.unarchive')}
          </Button>
        ) : null}
      </div>

      {confirming ? (
        <AlertDialog open onOpenChange={(open) => (open ? undefined : setConfirming(null))}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(confirming === 'archive' ? 'variants.archive.confirmTitle' : 'variants.archive.confirmUnarchiveTitle', {
                  name,
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(confirming === 'archive' ? 'variants.archive.confirmBody' : 'variants.archive.confirmUnarchiveBody')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-11">{t('variants.archive.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="h-11" onClick={() => run(confirming)}>
                {t('variants.archive.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  )
}
